import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ContractSummary } from '@/api/schemas';
import { ContractStatusBadge } from '@/components/ContractStatusBadge';
import { SortableTableHead } from '@/components/search/SortableTableHead';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { VinLink } from '@/components/VinLink';
import { useClock } from '@/lib/clock';
import type { SortDirection, SortKey } from '@/lib/contractSort';
import { deriveContractStatus } from '@/lib/contractStatus';
import { NOT_AVAILABLE, orNotAvailable } from '@/lib/display';

const COLUMN_COUNT = 8;

type SortProps = {
  activeSortKey: SortKey | null;
  direction: SortDirection;
  onSort: (key: SortKey) => void;
};

function ContractRow({ contract }: { contract: ContractSummary }) {
  const today = useClock().today();

  return (
    <TableRow>
      <TableCell>
        {contract.coverageType ? (
          <Badge variant="neutral">{contract.coverageType}</Badge>
        ) : (
          NOT_AVAILABLE
        )}
      </TableCell>
      <TableCell>
        <ContractStatusBadge status={deriveContractStatus(contract, today)} />
      </TableCell>
      <TableCell>
        <VinLink vin={contract.vin} compact variant="muted" />
      </TableCell>
      <TableCell>{orNotAvailable(contract.customerName)}</TableCell>
      <TableCell>
        <Link to={`/contracts/${contract.id}`} className="hover:underline">
          {contract.policyNumber}
        </Link>
      </TableCell>
      <TableCell>
        {contract.paymentAgreement ? (
          <Link
            to={`/agreements/${encodeURIComponent(contract.paymentAgreement.agreementId)}`}
            className="hover:underline"
          >
            {contract.paymentAgreement.agreementId}
          </Link>
        ) : (
          NOT_AVAILABLE
        )}
      </TableCell>
      <TableCell>{contract.policyNumber}</TableCell>
      <TableCell>{contract.dealerAccount}</TableCell>
    </TableRow>
  );
}

export function ContractResultsTable({
  contracts,
  placeholder,
  ...sort
}: SortProps & {
  contracts: readonly ContractSummary[];
  placeholder?: ReactNode;
}) {
  return (
    <div className="mx-6 [&_tbody_tr:last-child]:border-b [&_thead_tr]:border-t [&_td]:h-11 [&_td]:overflow-hidden [&_td]:text-ellipsis [&_td]:whitespace-nowrap [&_td]:px-3 [&_td]:py-2 [&_th]:px-3 [&_th]:py-2 [&_tr]:h-11">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <SortableTableHead
              label="Type"
              sortKey="type"
              className="w-[110px]"
              {...sort}
            />
            <SortableTableHead
              label="Status"
              sortKey="status"
              className="w-[110px]"
              {...sort}
            />
            <SortableTableHead
              label="VIN"
              sortKey="vin"
              className="w-[170px]"
              {...sort}
            />
            <SortableTableHead
              label="Customer Name"
              sortKey="customer"
              className="w-[160px]"
              {...sort}
            />
            <SortableTableHead
              label="Contract #"
              sortKey="contract"
              className="w-[130px]"
              {...sort}
            />
            <TableHead className="w-[110px]">Agreement #</TableHead>
            <SortableTableHead
              label="Policy #"
              sortKey="policy"
              className="w-[110px]"
              {...sort}
            />
            <SortableTableHead
              label="Dealer Account"
              sortKey="dealer"
              className="w-[130px]"
              {...sort}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {placeholder ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={COLUMN_COUNT} className="p-0">
                {placeholder}
              </TableCell>
            </TableRow>
          ) : (
            contracts.map((contract) => (
              <ContractRow key={contract.id} contract={contract} />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
